import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PipelineStage, User } from '../types';

interface AppState {
  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Auth
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;

  // Theme
  themePreference: 'light' | 'dark';
  setThemePreference: (theme: 'light' | 'dark') => void;

  // Language
  languagePreference: string;
  setLanguagePreference: (lang: string) => void;

  // Deal detail — pinned tab per pipeline stage. When the user opens a
  // deal whose stage matches a pinned entry, the corresponding tab opens
  // by default instead of the global ``visao360`` fallback. Persisted to
  // localStorage so the preference survives reloads / re-logins.
  pinnedTabsByStage: Partial<Record<PipelineStage, string>>;
  setPinnedTabByStage: (stage: PipelineStage, tabKey: string) => void;
  clearPinnedTabForStage: (stage: PipelineStage) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Sidebar
      sidebarCollapsed: false,
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      // Auth
      currentUser: null,
      setCurrentUser: (user) => set({ currentUser: user }),

      // Theme
      themePreference: 'light',
      setThemePreference: (theme) => set({ themePreference: theme }),

      // Language
      languagePreference: 'pt-BR',
      setLanguagePreference: (lang) => set({ languagePreference: lang }),

      // Pinned tab per stage
      pinnedTabsByStage: {},
      setPinnedTabByStage: (stage, tabKey) =>
        set((state) => ({
          pinnedTabsByStage: { ...state.pinnedTabsByStage, [stage]: tabKey },
        })),
      clearPinnedTabForStage: (stage) =>
        set((state) => {
          const next = { ...state.pinnedTabsByStage };
          delete next[stage];
          return { pinnedTabsByStage: next };
        }),
    }),
    {
      name: 'janus-app-store',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        themePreference: state.themePreference,
        languagePreference: state.languagePreference,
        pinnedTabsByStage: state.pinnedTabsByStage,
      }),
    },
  ),
);
